import React, { ReactNode } from 'react'

import { twMerge } from 'tailwind-merge'
import { tv } from 'tailwind-variants'

import { Image, type ImageProps } from '@components/Image'

import SectionCTA from './SectionCTA'
import SectionContent from './SectionContent'
import SectionDescription from './SectionDescription'
import SectionHeader from './SectionHeader'
import SectionTitle from './SectionTitle'

interface SectionProps {
  children: ReactNode
  className?: string
  backgroundColor?: string
  image?: ImageProps
  theme?: 'light' | 'dark'
  stickyOnBorder?: boolean
  extraClass?: string
}

interface SectionComponent extends React.FC<SectionProps> {
  Header: typeof SectionHeader
  Content: typeof SectionContent
  Description: typeof SectionDescription
  Title: typeof SectionTitle
  CTA: typeof SectionCTA
}

export const Section: SectionComponent = ({
  children,
  className = '',
  backgroundColor,
  image,
  theme = 'light',
  stickyOnBorder = false,
  extraClass = '',
}) => {
  const {
    src = '',
    srcset = {},
    alt = '',
    focalPoint = { x: '', y: '' },
  } = image || {}

  const backgroundStyles = tv({
    base: 'section',
    variants: {
      theme: {
        light: 'bg-white',
        dark: 'bg-black',
      },
      blurry: {
        true: '',
      },
    },
    defaultVariants: {
      theme: 'light',
    },
    compoundVariants: [
      { theme: 'light', blurry: true, class: 'bg-white/50' },
      { theme: 'dark', blurry: true, class: 'bg-black/50' },
    ],
  })

  return (
    <section
      className={twMerge(backgroundStyles({ theme }), `section ${className}`)}
      style={backgroundColor ? { backgroundColor } : undefined}
    >
      {image && image.src ? (
        <>
          <div className="flex">
            <div className="w-sidebar-mobile relative md:min-w-[497px]">
              <div
                className={twMerge(
                  backgroundStyles({ theme, blurry: true }),
                  `w-sidebar-mobile md:w-sidebar-desktop absolute inset-y-0 z-20 backdrop-blur-[80px]`,
                  theme === 'dark' ? 'hidden md:block' : 'block',
                )}
              ></div>
              <Image
                src={src}
                srcset={srcset}
                alt={alt}
                focalPoint={focalPoint}
              />
            </div>
            <div
              className={`relative flex-1 pl-9 py-[60px] md:py-[100px] 
                ${
                  image && image.src
                    ? 'w-[calc(100%-theme(spacing.sidebar-mobile)-497px)]'
                    : 'w-[calc(100%-theme(spacing.sidebar-mobile))]'
                }
                ${stickyOnBorder ? 'ltr:md:pr-0 rtl:md:pl-0' : 'md:px-12'}
              `}
            >
              <div className="relative z-20">{children}</div>
              <div
                className={twMerge(
                  backgroundStyles({ theme, blurry: theme === 'dark' }),
                  `absolute inset-0 z-10 backdrop-blur-[150px]`,
                )}
              ></div>
              <Image
                src={src}
                srcset={srcset}
                alt={alt}
                focalPoint={focalPoint}
                className="absolute inset-0 object-cover"
              />
            </div>
          </div>
        </>
      ) : (
        <div className={twMerge('py-[60px] md:py-[100px]', extraClass)}>
          {children}
        </div>
      )}
    </section>
  )
}

Section.Header = SectionHeader
Section.Content = SectionContent
Section.Description = SectionDescription
Section.Title = SectionTitle
Section.CTA = SectionCTA
